import { Component, EventEmitter, Inject, OnInit, SecurityContext } from '@angular/core';
import { DatePipe } from '@angular/common';
import { DomSanitizer } from '@angular/platform-browser';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { DeviceDetectorService } from 'ngx-device-detector';
import { DialogButton } from '@snapon/design-system-angular';
import { AppInfoService } from 'core/app-info/app-info.service';
import { CountryCode } from 'core/app-info/country-specifics.enum';
import { TouchpointsService } from '../services/touchpoints.service';
import { EditTouchpointComponent } from '../edit-touchpoint/edit-touchpoint.component';
import {
  Touchpoint,
  TouchpointAttachment,
  TouchpointComment,
  TOUCHPOINT_DETAIL_ACTIONS,
  TouchpointType
} from '../entities/touchpoints-entity';

@Component({
  selector: 'app-touchpoint-detail',
  templateUrl: './touchpoint-detail.component.html',
  styleUrls: ['./touchpoint-detail.component.scss'],
  standalone: false,
  providers: [DatePipe]
})
export class TouchpointDetailComponent implements OnInit {
  appStrings = this.appInfoService.appStrings;
  isMobile = false;
  private readonly typeLabels: Record<number, string> = {
    1: 'Meeting', 2: 'Call', 3: 'Appointment', 4: 'Task'
  };

  dateTimeFormat = this.appInfoService.dateTimeFormat;
  readonly accountDashboardUrl = this.touchpointsService.getAccountDashboardUrl(this.touchpoint);
  deleteDialogOpen$ = new EventEmitter<boolean>();
  private deleteEntireSeries = false;
  isLoading = false;
  touchpointNotes: TouchpointComment[] = [];
  isLoadingNotes = false;
  deleteDialogButtons: DialogButton[] = [
    { label: 'Cancel', type: 'secondary' },
    {
      label: 'Delete',
      type: 'primary',
      onclick: () => {
        this.isLoading = true;
        this.touchpointsService.deleteTouchpoint(
          this.touchpoint.id,
          this.deleteEntireSeries,
          null,
          () => this.dialogRef.close({ deleted: true }),
          this.isOutlookOnly,
          this.touchpoint.employeeId
        );
      }
    }
  ];

  constructor(
    public dialogRef: MatDialogRef<TouchpointDetailComponent>,
    private dialog: MatDialog,
    private appInfoService: AppInfoService,
    private touchpointsService: TouchpointsService,
    private datePipe: DatePipe,
    private sanitizer: DomSanitizer,
    private deviceService: DeviceDetectorService,
    @Inject(MAT_DIALOG_DATA) public touchpoint: Touchpoint,
  ) { }

  ngOnInit(): void {
    this.isMobile = this.deviceService.isMobile();
    this.loadNotes();
  }

  get attachments(): TouchpointAttachment[] {
    return this.touchpoint.attachments ?? [];
  }

  private loadNotes(): void {
    this.isLoadingNotes = true;
    this.touchpointsService.getComments(this.touchpoint.id, this.isOutlookOnly).subscribe({
      next: notes => {
        this.touchpointNotes = notes ?? [];
        this.isLoadingNotes = false;
      },
      error: () => this.isLoadingNotes = false
    });
  }

  get isOutlookOnly(): boolean {
    return this.touchpoint.isOutlookOnly === true;
  }

  /** Archived touchpoints are read-only; they can still be deleted, never edited. */
  get isArchived(): boolean {
    return this.touchpoint.isArchived === true;
  }

  get formattedDateTime(): string {
    const start = new Date(this.touchpoint.startDate);
    const end = new Date(this.touchpoint.endDate);
    const startDateTime = this.datePipe.transform(start, this.appInfoService.dateTimeFormat) ?? '';
    const endTime = this.datePipe.transform(end, this.appInfoService.timeFormat) ?? '';
    return `${startDateTime} - ${endTime}`;
  }

  get typeLabel(): string {
    return this.typeLabels[this.touchpoint.touchpointType] ?? '';
  }

  get recurrenceLabel(): string {
    return this.touchpointsService.getRecurrenceLabel(this.touchpoint);
  }

  private get isUsLocale(): boolean {
    return (this.appInfoService.userData?.UserInfo?.CountryCode || CountryCode.US) === CountryCode.US;
  }

  /** Impact-generated Call touchpoints dial the contact directly; Outlook ones expose a meeting dial-in instead. */
  private get firstContactPhone(): string {
    if (!this.touchpoint.isImpactGeneratedTouchpoint || this.touchpoint.touchpointType !== Number(TouchpointType.Call)) {
      return '';
    }
    return (this.touchpoint.contacts ?? []).find(c => !c.isDeleted && c.phoneNumber)?.phoneNumber ?? '';
  }

  /** Graph returns a single toll number tied to the organizer's tenant, so it is US-only. */
  private get tollNumber(): string {
    return this.isUsLocale ? (this.touchpoint.tollNumber ?? '') : '';
  }

  /** Outside the US the Teams dial-in page lists local numbers, avoiding international charges. */
  private get dialInUrl(): string {
    return this.isUsLocale ? '' : (this.touchpoint.dialInUrl ?? '');
  }

  get showCallButton(): boolean {
    return Boolean(this.firstContactPhone || this.tollNumber || this.dialInUrl);
  }

  onCall(): void {
    const phoneNumber = this.firstContactPhone || this.tollNumber;
    if (phoneNumber) {
      window.location.href = this.buildTelUri(phoneNumber);
      return;
    }
    if (this.dialInUrl) {
      window.open(this.dialInUrl, '_blank', 'noopener,noreferrer');
    }
  }

  /**
   * Builds a dialable tel: URI from a raw number, e.g. '+1 425-555-0100,,12345678#'.
   * @param phoneNumber the contact phone or Graph-supplied toll number
   */
  private buildTelUri(phoneNumber: string): string {
    const allowed = phoneNumber.replace(/[^\d+,#*]/g, '');
    const normalized = (allowed.startsWith('+') ? '+' : '') + allowed.replace(/\+/g, '');
    if (this.isIos) {
      return `tel:${normalized.replace(/[#*]/g, '')}`;
    }
    return `tel:${normalized.replace(/#/g, '%23')}`;
  }

  private get isIos(): boolean {
    return this.deviceService.os()?.toLowerCase() === 'ios';
  }

  /** Lead touchpoints carry their name in companyName, customer ones in customerName. */
  get accountName(): string {
    return (this.touchpoint.companyName || this.touchpoint.customerName)?.trim() ?? '';
  }

  get showLastVisited(): boolean {
    return !this.isOutlookOnly && Boolean(this.accountDashboardUrl);
  }

  /**
   * @param event - Link click event
   */
  onAccountNameClick(event: MouseEvent): void {
    event.preventDefault();
    this.dialogRef.close({ action: TOUCHPOINT_DETAIL_ACTIONS.accountDashboard });
  }

  onLastVisited(): void {
    this.dialogRef.close({ action: TOUCHPOINT_DETAIL_ACTIONS.lastVisited });
  }

  get contactNames(): string[] {
    const fromContacts = (this.touchpoint.contacts ?? [])
      .filter(c => !c.isDeleted && c.name)
      .map(c => c.name);
    if (fromContacts.length) {
      return fromContacts;
    }
    if (this.touchpoint.firstContactName) {
      return this.touchpoint.firstContactName.split(', ').filter(n => n.trim());
    }
    if (this.touchpoint.firstContactDisplay) {
      return [this.touchpoint.firstContactDisplay];
    }
    return [];
  }

  get notesHtml(): string {
    if (!this.touchpoint.notes) {
      return '';
    }
    const escapeText = (s: string) => s
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/\n/g, '<br>');
    const urlRegex = /https?:\/\/[^\s<>"]+/g;
    const text = this.touchpoint.notes;
    let result = '';
    let lastIndex = 0;
    let match: RegExpExecArray | null;
    while ((match = urlRegex.exec(text)) !== null) {
      result += escapeText(text.slice(lastIndex, match.index));
      const url = match[0];
      result += `<a href="${url.replace(/&/g, '&amp;')}" target="_blank" rel="noopener noreferrer">${escapeText(url)}</a>`;
      lastIndex = urlRegex.lastIndex;
    }
    result += escapeText(text.slice(lastIndex));
    return this.sanitizer.sanitize(SecurityContext.HTML, result) ?? '';
  }

  /**
   * Resolves which touchpoint id an action should target. The action always targets the
   * specific touchpoint instance; whether it applies to the whole series is conveyed via
   * the updateRecurrence flag, not by swapping in the series master id.
   */
  onDeleteThisTouchpoint(wholeSeries = false): void {
    this.deleteEntireSeries = wholeSeries;
    this.deleteDialogOpen$.emit(true);
  }

  onEditThisTouchpoint(wholeSeries = false): void {
    this.dialog.open(EditTouchpointComponent, {
      data: {
        customerId: this.touchpoint.customerId,
        leadId: this.touchpoint.leadId,
        touchpointId: this.touchpoint.id,
        accountManagerId: this.touchpoint.accountManagerId,
        employeeId: this.touchpoint.employeeId,
        updateRecurrence: wholeSeries,
        isOutlookOnly: this.isOutlookOnly,
      },
      panelClass: ['ds', 'fullWidth', this.isMobile ? 'fullScreen' : ''],
    }).afterClosed().subscribe(result => {
      if (result) {
        this.dialogRef.close({ edited: true });
      }
    });
  }

  onJoinMeeting(): void {
    if (!this.touchpoint.joinUrl) {
      return;
    }
    window.open(this.touchpoint.joinUrl, '_blank', 'noopener,noreferrer');
  }

  onCopyMeetingLink(): void {
    if (!this.touchpoint.joinUrl) {
      return;
    }
    navigator.clipboard.writeText(this.touchpoint.joinUrl);
  }

}
